/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    ButtonService, CLASS_NAME_OF_TIPS_OVER_HEADER, EventService, HttpService,
    I18nService, LayoutComponent, LocalStorageService, LogService,
    PermissionService, PromptService, SessionStorageService, UtilService,
} from '@dm/core';
import { ALL_VSTORE_ID, STORAGE_KEY } from 'app/common/constants';
import { AuthService } from 'app/common/services/auth.service';
import { ShardSwitch, SWITCH } from 'app/config';
import { environment } from 'environments/environment';

import { EventServiceKeyE } from './common/constants/event-service-key';
import { SIMPLE_MODE_PATH } from './common/constants/simple-mode.const';
import { SimpleModeService } from './common/services/simple-mode.service';
import { ToolService } from './common/services/tool.service';
import { type OEM } from './common/types/oem';
import { FeatureConfig } from './config/feature';
import { type GuideConfig } from './feature/share/smart-assistant/smart-assistant';
import { GuideEventKey, GuideStorageKey, routerAndEntryMap, SmartAssistantEntryE } from './feature/share/smart-assistant/smart-assistant.const';
import { SmartAssistantService } from './feature/share/smart-assistant/smart-assistant.service';
import { ASSISTANT_WIDTH, FULL_WIDTH } from './feature/simple-mode/simple-mode.const';

const copyrightEn = 'Copyright © Huawei Technologies Co., Ltd. 2013-2022. All rights reserved.';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    host: {
        '[class.app-root-simple]': 'isSimpleMode',
    },
})
export class AppComponent implements OnInit {
    @ViewChild('zoomDontPrompt') zoomDontPrompt: TemplateRef<any>;
    oem: OEM = (window as any).oem;
    language = this.i18nService.language === 'zh' ? 'English' : '中文';
    SUPPORT_JA_LAN = FeatureConfig.IsSupportJaLan;
    copyright = this.getCopyRight();

    /**
     * 登录页面
     */
    isLoginPage = false;
    /**
     * 初始化页面
     */
    isInitializePage = false;
    /**
     * 开工失败页面
     */
    isPoweronFailPage = false;
    showZoomTip = true;
    noNoticeAgain = this.i18nService.get('home.quickstart_task_noNoticeAgain_checkbox');
    dontPrompt = false;
    /**
     * 极简模式开启智能助手后，宽度会变化
     */
    dynamicWidth = FULL_WIDTH;
    /**
     * 智能助手的宽度（改了该值，需要同步修改app-root-simple-assistant的margin）
     */
    ASSISTANT_WIDTH = ASSISTANT_WIDTH;
    /**
     * 展示智能助手
     */
    showSmartAssistant = false;

    /**
     * header上方提示容器类名(用于core获取上方提示高度)
     */
    CLASS_NAME_OF_TIPS_OVER_HEADER = CLASS_NAME_OF_TIPS_OVER_HEADER;

    /**
     * 是否默认进入极简，初始化需要适配极简样式
     */
    isAutoInSimple = false;

    get isSimpleMode(): boolean {
        return SWITCH.IS_SIMPLE_MODE;
    }

    isLeftMenuExpand = true;
    SWITCH = SWITCH;
    ShardSwitch = ShardSwitch;
    isOem: boolean;

    /**
     * 是否有DME IQ权限
     */
    hasCallhomeR: boolean;
    /**
     * 是否是远程协助用户（不显示顶部协助信息）
     */
    isRemoteAssistanceUser: boolean;

    constructor(
        private authService: AuthService,
        private i18nService: I18nService,
        private router: Router,
        private httpService: HttpService,
        private utilService: UtilService,
        private promptService: PromptService,
        private buttonService: ButtonService,
        private eventService: EventService,
        private logService: LogService,
        private sessionStorageService: SessionStorageService,
        private simpleModeService: SimpleModeService,
        private smartAssistantService: SmartAssistantService,
        private toolService: ToolService,
        private permissionService: PermissionService,
        private localStorageService: LocalStorageService,
    ) {
        document.documentElement.classList.add('lang-' + (this.i18nService.language === 'ja' ? 'en' : this.i18nService.language));
        document.title = this.oem.vendor !== 'huawei' ?
            this.oem.product :
            '';

        // 页面卸载时将超时例外关闭
        window.onbeforeunload = (): void => {
            this.authService.disableTimeoutExcept();
        };

        // 配置vstore的rest
        LayoutComponent.prototype.vstoreRest = this.httpService.makeUrl('vstore.getBatch').setParams({ selectFields: 'ID,NAME' });
        LayoutComponent.prototype.singleVstoreRest = this.httpService.makeUrl('vstore.get');

        // release下清空日志，避免旧版本残留日志引起安全风险
        if (environment.env === 'release' || environment.env === 'release_crack') {
            this.logService.clear();
        }
    }

    ngOnInit(): void {
        this.isOem = this.toolService.isOem();
        this.watchRouterChange();
        this.checkBrowserZoom();

        this.eventService.subscribe('systemInfoUpdate', (data) => {
            if (data && data.NAME) {
                let str = data.NAME;
                if (data && data.LOCATION) {
                    const location = this.utilService.decodeHTML(data.LOCATION);
                    str += `(${location})`;
                }
                document.title = str;
            }
        });

        this.eventService.subscribe(EventServiceKeyE.SimpleMenuExpandChange, (isExpand: boolean) => {
            this.isLeftMenuExpand = isExpand;
        });

        this.eventService.subscribe(GuideEventKey.assistantConfigChange, (params: GuideConfig) => {
            if (this.showSmartAssistant !== params.showSmartAssistant) {
                this.dynamicWidth = params.showSmartAssistant ? `calc(${FULL_WIDTH} - ${ASSISTANT_WIDTH})` : FULL_WIDTH;
                this.showSmartAssistant = params.showSmartAssistant;
            }
        });
    }

    watchRouterChange(): void {
        this.router.events.subscribe((evt: any) => {
            if (evt instanceof NavigationEnd) {
                this.hasCallhomeR = this.permissionService.hasPermission('call_home_R');
                this.isRemoteAssistanceUser = this.authService.isRemoteAssistanceUser();

                this.isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/login/sso';
                this.isInitializePage = window.location.pathname === '/initialize';
                this.isPoweronFailPage = window.location.pathname === '/poweronfail';

                this.isAutoInSimple = window.location.pathname === '/initialize' && window.location.search === '?isAutoInSimple=true';
                this.changeSimplyMode(window.location.pathname);
                this.changeSmartAssistant(window.location.pathname);
            }
        });
    }

    /**
     * 根据路由变化判断是否是极简模式
     * 由于极简模式下存在其他按钮、链接能够直接切换到高级模式，所以通过路由变化来进行判断
     * @param pathName
     */
    changeSimplyMode(pathName: string): void {
        if (pathName.startsWith('/simple')) {
            this.simpleModeService.setSimpleMode(true);
        } else {
            if (SWITCH.IS_SIMPLE_MODE) {
                // 从极简模式切换到高级模式，切换到所有租户
                this.sessionStorageService.set('vstoreId', ALL_VSTORE_ID);
            }
            this.simpleModeService.setSimpleMode(false);
        }
    }

    /**
     * 当智能助手开启时，根据路由变化，展示智能助手内容
     * @param pathName
     */
    changeSmartAssistant(pathName: string): void {
        if (this.isLoginPage) {
            this.smartAssistantService.setGuideConfig(GuideStorageKey.showSmartAssistant, false);
            this.smartAssistantService.initStatus();
        }

        if (!pathName.startsWith('/simple')) {
            return;
        }

        // 如果路由不是告警和事件，则隐藏智能助手的日志导出
        if (routerAndEntryMap[pathName] !== SmartAssistantEntryE.AlarmAndEvent) {
            this.smartAssistantService.setGuideConfig(GuideStorageKey.showExportLog, false);
        }
        this.smartAssistantService.assistantEntryChange(routerAndEntryMap[pathName]);

        // 如果路由不是LUN或主机，需要清空引导中“创建更多”操作的缓存
        if (![SIMPLE_MODE_PATH.LUN, SIMPLE_MODE_PATH.HOST].includes(pathName)) {
            this.smartAssistantService.setGuideConfig(GuideStorageKey.stepForCreateMore, null);
        }
    }

    /**
     * 修改语言
     */
    changeLan(value: string): void {
        if (this.i18nService.language === value) {
            return;
        }

        this.i18nService.changeLanguage();
    }

    /**
     * 检测浏览器缩放，当缩放比例不为100%时提示用户
     */
    checkBrowserZoom(): void {
        // 如果已经提示过用户已忽略并继续则不再检测
        if (!this.showZoomTip || this.localStorageService.get(STORAGE_KEY.ZOOM_CHECK)) {
            return;
        }

        let isPromptOpen = false;
        let fn = this.utilService.interval(() => {
            const ratio = this.getZoom();
            if (ratio !== 100 && !isPromptOpen) {
                this.promptService.prompt({
                    title: this.i18nService.get('common.browser_show_confirm_title'),
                    content: this.i18nService.get('common.browser_show_confirm_msg'),
                    closeBtn: false,
                    templateRef: this.zoomDontPrompt,
                    btns: [
                        this.buttonService.create({
                            id: 'zoom-check-confirm',
                            label: this.i18nService.get('common.browser_show_ignore_btn'),
                            command: () => {
                                this.promptService.close();
                                isPromptOpen = false;
                                this.showZoomTip = false;
                                if (this.dontPrompt) {
                                    this.localStorageService.set(STORAGE_KEY.ZOOM_CHECK, true);
                                }
                                fn();
                                fn = null;
                            },
                        }),
                    ],
                });
                isPromptOpen = true;
            } else if (ratio === 100 && isPromptOpen) {
                this.promptService.close();
                isPromptOpen = false;
            } else {
                // cleancode 含else if分支的条件判断应在最后加一个else分支
            }
        }, 1000);
    }

    getCopyRight(): string {
        let copyright = '';

        if (this.oem.vendor !== 'huawei') {
            copyright = this.i18nService.language === 'zh' ? this.oem.copyright_zh : this.oem.copyright_en;
        } else {
            copyright = this.i18nService.language === 'ja' ? copyrightEn : this.i18nService.get('common.copyright_label');
        }

        return copyright;
    }

    /**
     * 获取缩放比例
     */
    private getZoom(): number {
        let ratio = window.devicePixelRatio !== undefined ?
            window.devicePixelRatio :
            window.outerWidth / window.innerWidth;

        if (ratio) {
            ratio = Math.round(ratio * 100);
        }

        return ratio;
    }
}
